// Deterministic terrain heightfield — exact JS mirror of
// internal/terrain/terrain.go. Both sides use only uint32 wrap-around
// arithmetic and IEEE-754 float64 add/sub/mul/div/sqrt/floor, so heights are
// bit-identical between the Go server and the browser. Verified by
// tools/check_terrain_parity.mjs; keep the two files in lockstep. (JS never
// fuses a multiply into an add; the Go side spells float64(a*b) everywhere
// so the arm64 server doesn't either — see the FMA note in terrain.go.)

// The corner-height cache under groundAt: tiles of 32x32 grid corners (384 m
// at the 12 m grid), filled on first touch. The chunk builder and every
// ground query hit the same few hundred tiles around the camera, so each
// corner's noise is evaluated once instead of once per chunk, clutter tuft
// and camera clamp. Bounded, oldest-first: a long sortie across the map
// recycles tiles it flew away from rather than keeping the whole world.
const CORNER_TILE = 32;
const CORNER_TILES_MAX = 768; // ~6 MB of float64 corners

function hash2(ix, iz, seed) {
  let h =
    ((Math.imul(ix, 0x27d4eb2d) >>> 0) +
      (Math.imul(iz, 0x165667b1) >>> 0) +
      (Math.imul(seed, 0x9e3779b9) >>> 0)) >>>
    0;
  h = (h ^ (h >>> 15)) >>> 0;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  return h / 4294967296;
}

function valueNoise(x, z, seed) {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const tx = x - x0;
  const tz = z - z0;
  const ix = x0 | 0;
  const iz = z0 | 0;
  const sx = tx * tx * (3 - 2 * tx);
  const sz = tz * tz * (3 - 2 * tz);
  const v00 = hash2(ix, iz, seed);
  const v10 = hash2(ix + 1, iz, seed);
  const v01 = hash2(ix, iz + 1, seed);
  const v11 = hash2(ix + 1, iz + 1, seed);
  const a = v00 + (v10 - v00) * sx;
  const b = v01 + (v11 - v01) * sx;
  return a + (b - a) * sz;
}

function fbm(x, z, seed, octaves) {
  let sum = 0.0;
  let norm = 0.0;
  let amp = 1.0;
  let freq = 1.0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise(x * freq, z * freq, (seed + i * 1013) >>> 0);
    norm += amp;
    amp *= 0.5;
    freq *= 2.0;
  }
  return sum / norm;
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function smoothstep(e0, e1, x) {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}

// Parity-safe quartic falloff (mirrors bump in armada.go): 1 at z0, 0 beyond
// |z-z0| >= w. Squared distance only — no Math.abs.
function bump(z, z0, w) {
  const t = (z - z0) / w;
  const v = 1 - t * t;
  if (v < 0) return 0;
  return v * v;
}

// Fold across the z=0 front line for the armada's noise inputs, so fixed
// sides fight on mirror-image ground — the northern (z<0) draw kept, its
// mirror handed to the south (mirrors armadaMirrorZ in armada.go —
// comparison + negation only, no Math.abs, both parity-exact).
function armadaMirrorZ(z) {
  if (z > 0) return -z;
  return z;
}

// Town street-grid pitch (mirrors landBuildingGridM in land.go).
export const LAND_BUILDING_GRID_M = 30.0;
// Forest canopy sightline height (mirrors LandCanopyM in land.go).
export const LAND_CANOPY_M = 13.0;

export class TerrainField {
  /**
   * @param {{seed:number, radiusM:number, waterLevelM:number,
   *          cellSizeM:number, blockHeightM:number, maxTerrainHeightM:number}} world
   */
  constructor(world) {
    this.seed = world.seed >>> 0;
    this.radius = world.radiusM;
    this.waterLevel = world.waterLevelM;
    this.cellSize = world.cellSizeM;
    this.blockHeight = world.blockHeightM;
    this.maxHeight = world.maxTerrainHeightM;
    this.theater = world.theater || "";
    this._tiles = new Map(); // corner-height cache (see CORNER_TILE)
  }

  heightAt(x, z) {
    if (this.theater === "naval") return this.navalHeightAt(x, z);
    if (this.theater === "land") return this.landHeightAt(x, z);
    if (this.theater === "armada") return this.armadaHeightAt(x, z);
    const continent = fbm(x * (1.0 / 2600.0), z * (1.0 / 2600.0), this.seed, 3);
    const detail = fbm(
      x * (1.0 / 420.0) + 37.0,
      z * (1.0 / 420.0) - 71.0,
      (this.seed + 5077) >>> 0,
      4,
    );
    const r = Math.sqrt(x * x + z * z);
    const centerBias = clamp01(1.0 - r / 5200.0);
    const land = smoothstep(0.5, 0.66, continent * 0.72 + centerBias * 0.34);
    const d2 = detail * detail;
    return -14.0 * (1.0 - land) + land * (6.0 + d2 * this.maxHeight);
  }

  // Ocean-dominant World-of-Warships heightfield — exact mirror of
  // navalHeightAt in internal/terrain/terrain.go. Open sea, scattered low
  // islands, a ragged northern coastline.
  navalHeightAt(x, z) {
    const nz = z / this.radius;
    const cont = fbm(
      x * (1.0 / 2200.0) + 13.0,
      z * (1.0 / 2200.0) + 5.0,
      (this.seed + 991) >>> 0,
      4,
    );
    const isl = fbm(
      x * (1.0 / 780.0) + 19.0,
      z * (1.0 / 780.0) + 7.0,
      (this.seed + 4231) >>> 0,
      4,
    );
    const detail = fbm(
      x * (1.0 / 360.0) + 37.0,
      z * (1.0 / 360.0) - 71.0,
      (this.seed + 5077) >>> 0,
      4,
    );
    const coast = clamp01((-nz - 0.3) * 2.2);
    const northland = smoothstep(0.45, 0.62, cont * 0.55 + coast);
    const islands = smoothstep(0.66, 0.74, isl);
    let land = northland;
    if (islands > land) land = islands;
    const d2 = detail * detail;
    return -26.0 * (1.0 - land) + land * (3.0 + d2 * this.maxHeight * 0.42);
  }

  // --- Combined-arms theater (theater "armada") ---------------------------
  // Exact mirror of internal/terrain/armada.go: the Brick Gulf — the land
  // theater's steppe west of an authored coastline, the naval theater's sea
  // east of it, towns flattened after the blend so coastal objectives stay
  // dry. Parity-safe ops only; see the Go file for the design commentary.

  /** The armada theater's authored town plan (see ArmadaTowns in Go). */
  armadaTowns() {
    if (this._atowns) return this._atowns;
    this._atowns = [
      { x: -6100, z: 0, r: 190, h: 12 },
      { x: -3700, z: 0, r: 170, h: 14 },
      { x: -1900, z: 0, r: 200, h: 5 },
      { x: 2500, z: -4300, r: 160, h: 4 },
      { x: 2500, z: 4300, r: 160, h: 4 },
      // HQ airfield plateaus and coast-battery pads are bare: graded flat,
      // never built up (mirrors Bare in armada.go).
      { x: -4900, z: -5400, r: 150, h: 10, bare: true },
      { x: -4900, z: 5400, r: 150, h: 10, bare: true },
      { x: -3900, z: -2200, r: 100, h: 9 },
      { x: -3900, z: 2200, r: 100, h: 9 },
      { x: 3100, z: -4900, r: 60, h: 8, bare: true },
      { x: 3100, z: 4900, r: 60, h: 8, bare: true },
    ];
    return this._atowns;
  }

  /** The active theater's town plan: armada's or the land theater's. */
  townPlan() {
    return this.theater === "armada" ? this.armadaTowns() : this.landTowns();
  }

  /** Meandering land/sea boundary at z: land west of it, sea east. */
  armadaCoastX(z) {
    let c = 900.0;
    c -= 2400 * bump(z, 0, 2500);
    c += 2400 * bump(z, -5000, 1900);
    c += 2400 * bump(z, 5000, 1900);
    const mz = armadaMirrorZ(z);
    const w = fbm(
      mz * (1.0 / 1400.0) + 53.0,
      mz * (1.0 / 700.0) - 29.0,
      (this.seed + 8887) >>> 0,
      3,
    );
    c += (w - 0.5) * 1600;
    return c;
  }

  armadaLandHeight(x, z, coast) {
    const mz = armadaMirrorZ(z);
    const base = fbm(
      x * (1.0 / 2300.0) + 7.0,
      mz * (1.0 / 2300.0) - 13.0,
      (this.seed + 7717) >>> 0,
      3,
    );
    const hills = fbm(
      x * (1.0 / 1150.0) + 31.0,
      mz * (1.0 / 1150.0) + 3.0,
      (this.seed + 2437) >>> 0,
      4,
    );
    const detail = fbm(
      x * (1.0 / 260.0) + 9.0,
      mz * (1.0 / 260.0) + 17.0,
      (this.seed + 911) >>> 0,
      3,
    );
    let h = 4.0 + base * 22.0;
    const d = coast - x;
    const coastFlat = smoothstep(400, 1800, d);
    const rear = smoothstep(2800, 6200, Math.sqrt(z * z));
    h += smoothstep(0.64, 0.72, hills) * (62.0 + 90.0 * rear) * coastFlat;
    h -= smoothstep(0.4, 0.26, hills) * 26.0 * coastFlat;
    h += detail * detail * 2.5;
    return h;
  }

  armadaSeaHeight(x, z, coast) {
    const mz = armadaMirrorZ(z);
    const isl = fbm(
      x * (1.0 / 780.0) + 19.0,
      mz * (1.0 / 780.0) + 7.0,
      (this.seed + 4231) >>> 0,
      4,
    );
    const detail = fbm(
      x * (1.0 / 360.0) + 37.0,
      mz * (1.0 / 360.0) - 71.0,
      (this.seed + 5077) >>> 0,
      4,
    );
    const d = x - coast;
    const openSea = smoothstep(900, 1800, d);
    let islands = smoothstep(0.67, 0.75, isl) * openSea;

    const fdx = x - 4300.0;
    const fdz = z - 0.0;
    const fort = smoothstep(620, 300, Math.sqrt(fdx * fdx + fdz * fdz));
    if (fort > islands) islands = fort;
    const ndx = x - 1300.0;
    const ndz = z + 1500.0;
    let bw = smoothstep(360, 170, Math.sqrt(ndx * ndx + ndz * ndz));
    if (bw > islands) islands = bw;
    const sdx = x - 1300.0;
    const sdz = z - 1500.0;
    bw = smoothstep(360, 170, Math.sqrt(sdx * sdx + sdz * sdz));
    if (bw > islands) islands = bw;

    const d2 = detail * detail;
    return -26.0 * (1.0 - islands) + islands * (3.0 + d2 * this.maxHeight * 0.42);
  }

  armadaHeightAt(x, z) {
    const coast = this.armadaCoastX(z);
    const d = x - coast;
    const m = smoothstep(-350, 350, d);
    let h;
    if (m <= 0) {
      h = this.armadaLandHeight(x, z, coast);
    } else if (m >= 1) {
      h = this.armadaSeaHeight(x, z, coast);
    } else {
      h =
        this.armadaLandHeight(x, z, coast) * (1 - m) +
        this.armadaSeaHeight(x, z, coast) * m;
    }
    for (const t of this.townPlan()) {
      const dx = x - t.x;
      const dz = z - t.z;
      const dd = Math.sqrt(dx * dx + dz * dz);
      const tm = smoothstep(t.r * 1.5, t.r * 0.7, dd);
      if (tm > 0) h = h * (1 - tm) + t.h * tm;
    }
    return h;
  }

  // --- Land-combat theater (theater "land") -------------------------------
  // Exact mirror of internal/terrain/land.go: a rolling drivable steppe with
  // steep-flanked hill masses, valley lakes, impenetrable forest blocks, and
  // flattened towns whose buildings come from the same lattice hash on both
  // sides. Parity-safe ops only.

  /** The fixed town plan, scaled off the map radius. */
  landTowns() {
    if (this._towns) return this._towns;
    const r = this.radius;
    this._towns = [
      { x: 0, z: 0, r: 190, h: 12 },
      { x: -0.4 * r, z: 0.14 * r, r: 140, h: 16 },
      { x: 0.4 * r, z: -0.14 * r, r: 140, h: 16 },
      { x: -0.1 * r, z: -0.42 * r, r: 130, h: 22 },
      { x: 0.1 * r, z: 0.42 * r, r: 130, h: 22 },
    ];
    return this._towns;
  }

  landHeightAt(x, z) {
    const base = fbm(
      x * (1.0 / 2300.0) + 7.0,
      z * (1.0 / 2300.0) - 13.0,
      (this.seed + 7717) >>> 0,
      3,
    );
    const hills = fbm(
      x * (1.0 / 1150.0) + 31.0,
      z * (1.0 / 1150.0) + 3.0,
      (this.seed + 2437) >>> 0,
      4,
    );
    const detail = fbm(
      x * (1.0 / 260.0) + 9.0,
      z * (1.0 / 260.0) + 17.0,
      (this.seed + 911) >>> 0,
      3,
    );
    let h = 4.0 + base * 22.0;
    h += smoothstep(0.64, 0.72, hills) * 62.0;
    h -= smoothstep(0.4, 0.26, hills) * 26.0;
    h += detail * detail * 2.5;
    for (const t of this.landTowns()) {
      const dx = x - t.x;
      const dz = z - t.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      const m = smoothstep(t.r * 1.5, t.r * 0.7, d);
      if (m > 0) h = h * (1 - m) + t.h * m;
    }
    return h;
  }

  /** Forest cover at a point (land/armada theaters): impenetrable treelines. */
  landForestAt(x, z) {
    if (this.theater !== "land" && this.theater !== "armada") return false;
    // Armada: fixed sides fight on mirror-image treelines (see land.go).
    const nz = this.theater === "armada" ? armadaMirrorZ(z) : z;
    const n = fbm(
      x * (1.0 / 520.0) + 101.0,
      nz * (1.0 / 520.0) - 57.0,
      (this.seed + 3169) >>> 0,
      3,
    );
    if (n < 0.655) return false;
    if (this.theater === "armada" && x > this.armadaCoastX(z)) return false;
    const h = this.heightAt(x, z);
    if (h < this.waterLevel + 1.5 || h > 58.0) return false;
    for (const t of this.townPlan()) {
      const dx = x - t.x;
      const dz = z - t.z;
      if (dx * dx + dz * dz < t.r * 1.35 * (t.r * 1.35)) return false;
    }
    return true;
  }

  /** Per-town hash seed (mirrors townSeed in land.go). */
  townSeed(t) {
    return (
      (this.seed +
        (Math.imul(t.x | 0, 2654435761) >>> 0) +
        (Math.imul(t.z | 0, 40503) >>> 0)) >>>
      0
    );
  }

  /**
   * Building occupying a town street-grid cell (gx, gz), or null. The
   * gx==0 / gz==0 rows are the open main streets.
   */
  townBuilding(t, gx, gz) {
    if (gx === 0 || gz === 0 || t.bare) return null;
    const grid = LAND_BUILDING_GRID_M;
    if (gx === 1 && gz === 1) {
      // The church lot (mirrors townChurchW/D/H in land.go): 26 m apse to
      // tower, 10 m across the nave, a 12 m ridge — the steeple above it is
      // the town's landmark.
      const cx = t.x + 1.5 * grid;
      const cz = t.z + 1.5 * grid;
      const dx = cx - t.x;
      const dz = cz - t.z;
      if (dx * dx + dz * dz > t.r * 0.85 * (t.r * 0.85)) return null;
      return { x: cx, z: cz, w: 26.0, d: 10.0, h: 12.0, church: true };
    }
    const seed = this.townSeed(t);
    const p = hash2(gx, gz, seed);
    if (p < 0.45) return null;
    let cx = t.x + (gx + 0.5) * grid;
    let cz = t.z + (gz + 0.5) * grid;
    cx += (hash2(gx, gz, (seed + 11) >>> 0) - 0.5) * 8.0;
    cz += (hash2(gx, gz, (seed + 23) >>> 0) - 0.5) * 8.0;
    const dx = cx - t.x;
    const dz = cz - t.z;
    if (dx * dx + dz * dz > t.r * 0.85 * (t.r * 0.85)) return null;
    const w = 10.0 + hash2(gx, gz, (seed + 37) >>> 0) * 8.0;
    const d = 10.0 + hash2(gx, gz, (seed + 53) >>> 0) * 8.0;
    const h = 6.0 + Math.floor(hash2(gx, gz, (seed + 71) >>> 0) * 3.0) * 4.0;
    return { x: cx, z: cz, w, d, h };
  }

  /** The building whose footprint covers (x, z), or null. */
  landBuildingAt(x, z) {
    if (this.theater !== "land" && this.theater !== "armada") return null;
    const grid = LAND_BUILDING_GRID_M;
    for (const t of this.townPlan()) {
      const dx = x - t.x;
      const dz = z - t.z;
      if (dx * dx + dz * dz > (t.r + grid) * (t.r + grid)) continue;
      const gx0 = Math.floor((x - t.x) / grid);
      const gz0 = Math.floor((z - t.z) / grid);
      for (let gi = gx0 - 1; gi <= gx0 + 1; gi++) {
        for (let gj = gz0 - 1; gj <= gz0 + 1; gj++) {
          const b = this.townBuilding(t, gi, gj);
          if (!b) continue;
          const ddx = x - b.x;
          const ddz = z - b.z;
          if (
            ddx >= -b.w * 0.5 &&
            ddx <= b.w * 0.5 &&
            ddz >= -b.d * 0.5 &&
            ddz <= b.d * 0.5
          ) {
            return b;
          }
        }
      }
    }
    return null;
  }

  /** Every building in one town (rendering). */
  townBuildings(t) {
    const n = Math.ceil(t.r / LAND_BUILDING_GRID_M);
    const out = [];
    for (let gx = -n; gx <= n; gx++) {
      for (let gz = -n; gz <= n; gz++) {
        const b = this.townBuilding(t, gx, gz);
        if (b) out.push(b);
      }
    }
    return out;
  }

  /** HeightAt at grid corner (ix, iz): the vertices every ground triangle is built from. */
  cornerHeight(ix, iz) {
    const tx = ix >> 5; // floor division by CORNER_TILE, negatives included
    const tz = iz >> 5;
    const key = (tx + 32768) * 65536 + (tz + 32768);
    let t = this._tiles.get(key);
    if (t === undefined) {
      t = new Float64Array(CORNER_TILE * CORNER_TILE);
      const cs = this.cellSize;
      for (let j = 0; j < CORNER_TILE; j++) {
        const z = (tz * CORNER_TILE + j) * cs;
        for (let i = 0; i < CORNER_TILE; i++) t[j * CORNER_TILE + i] = this.heightAt((tx * CORNER_TILE + i) * cs, z);
      }
      if (this._tiles.size >= CORNER_TILES_MAX) this._tiles.delete(this._tiles.keys().next().value);
      this._tiles.set(key, t);
    }
    return t[(iz & (CORNER_TILE - 1)) * CORNER_TILE + (ix & (CORNER_TILE - 1))];
  }

  /**
   * The ground: the triangle over (x, z) strung between cornerHeight's values,
   * each cell split on its (x0,z0)-(x1,z1) diagonal — the sea floor too, under
   * the water. Mirrors GroundAt in terrain.go; the chunk meshes in world.js are
   * exactly these triangles, so the drawn ground IS the collision ground.
   */
  groundAt(x, z) {
    const gx = x / this.cellSize;
    const gz = z / this.cellSize;
    const fx = Math.floor(gx);
    const fz = Math.floor(gz);
    const u = gx - fx;
    const v = gz - fz;
    const h00 = this.cornerHeight(fx, fz);
    const h11 = this.cornerHeight(fx + 1, fz + 1);
    if (u >= v) {
      const h10 = this.cornerHeight(fx + 1, fz);
      return h00 + (h10 - h00) * u + (h11 - h10) * v;
    }
    const h01 = this.cornerHeight(fx, fz + 1);
    return h00 + (h11 - h01) * u + (h01 - h00) * v;
  }

  /** Collision surface: the ground, but never below the ocean surface. */
  surfaceAt(x, z) {
    const t = this.groundAt(x, z);
    return t < this.waterLevel ? this.waterLevel : t;
  }

  /**
   * Unit up-facing normal of the ground triangle over (x, z), written into
   * `out` ([nx, ny, nz]) — what a hull tilts to. Mirrors NormalAt in Go.
   */
  normalAt(x, z, out = [0, 1, 0]) {
    const cs = this.cellSize;
    const gx = x / cs;
    const gz = z / cs;
    const fx = Math.floor(gx);
    const fz = Math.floor(gz);
    const h00 = this.cornerHeight(fx, fz);
    const h11 = this.cornerHeight(fx + 1, fz + 1);
    let sx, sz;
    if (gx - fx >= gz - fz) {
      const h10 = this.cornerHeight(fx + 1, fz);
      sx = (h10 - h00) / cs;
      sz = (h11 - h10) / cs;
    } else {
      const h01 = this.cornerHeight(fx, fz + 1);
      sx = (h11 - h01) / cs;
      sz = (h01 - h00) / cs;
    }
    const inv = 1 / Math.sqrt(sx * sx + sz * sz + 1);
    out[0] = -sx * inv;
    out[1] = inv;
    out[2] = -sz * inv;
    return out;
  }
}

export { hash2, fbm };
